import React from "react";
import { Box, Typography } from "@mui/material";
import { SalesPipelineStage } from "./types";

interface PipelineStageCardProps {
  stage: SalesPipelineStage;
  maxValue: number;
  icon: React.ReactNode;
  emphasis?: "normal" | "highlight" | "dim";
  /** Stagger index for lightweight CSS entrance (does not block render). */
  animationIndex?: number;
  onClick?: () => void;
}

const toneStyles = {
  default: {
    accent: "#d2363a",
    track: "#f3f4f6",
    iconBg: "#fff1f1",
    chipBg: "#ffffff",
  },
  success: {
    accent: "#0f9f6e",
    track: "#e8f8f1",
    iconBg: "#e8f8f1",
    chipBg: "#ffffff",
  },
  danger: {
    accent: "#c2410c",
    track: "#fff4ed",
    iconBg: "#fff4ed",
    chipBg: "#ffffff",
  },
};

const ProgressRing: React.FC<{
  value: number;
  maxValue: number;
  color: string;
  track: string;
  animationDelayMs?: number;
}> = ({ value, maxValue, color, track, animationDelayMs = 0 }) => {
  const size = 34;
  const stroke = 2.75;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = maxValue > 0 ? Math.min(1, value / maxValue) : 0;
  const offset = circumference * (1 - progress);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
        transform: "translateZ(14px)",
        filter: `drop-shadow(0 2px 4px ${color}40)`,
        "@keyframes pipelineRingDraw": {
          from: { strokeDashoffset: circumference },
          to: { strokeDashoffset: offset },
        },
        "& .pipeline-ring-progress": {
          animation: `pipelineRingDraw 0.5s ease-out ${animationDelayMs}ms both`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& .pipeline-ring-progress": {
            animation: "none",
          },
        },
      }}
    >
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={track}
          strokeWidth={stroke}
        />
        <circle
          className="pipeline-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <Typography
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Poppins', sans-serif",
          fontSize: "11px",
          fontWeight: 700,
          color: "#111827",
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const PipelineStageCard: React.FC<PipelineStageCardProps> = ({
  stage,
  maxValue,
  icon,
  emphasis = "normal",
  animationIndex = 0,
  onClick,
}) => {
  const tone = stage.tone || "default";
  const colors = toneStyles[tone];
  const isHighlight = emphasis === "highlight";
  const isDim = emphasis === "dim";
  const enterDelayMs = Math.min(animationIndex, 6) * 55;

  const baseShadow = isHighlight
    ? `0 0 0 5px ${colors.accent}18, 0 0 20px 6px ${colors.accent}22, 0 8px 18px rgba(15,23,42,0.08), 0 1px 0 rgba(255,255,255,0.85) inset`
    : `0 0 0 4px ${colors.accent}14, 0 0 16px 5px ${colors.accent}18, 0 6px 14px rgba(15,23,42,0.06), 0 1px 0 rgba(255,255,255,0.85) inset`;

  const hoverShadow = `0 0 0 6px ${colors.accent}1c, 0 0 24px 8px ${colors.accent}26, 0 10px 22px rgba(15,23,42,0.08), 0 1px 0 rgba(255,255,255,0.9) inset`;

  return (
    <Box
      sx={{
        flex: { xs: "none", md: "1 1 0" },
        width: { xs: "100%", md: "auto" },
        minWidth: { md: 0 },
        perspective: "800px",
        "@keyframes pipelineStageIn": {
          from: {
            opacity: 0,
            transform: "translateY(12px) rotateX(8deg) scale(0.96)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0) rotateX(0deg) scale(1)",
          },
        },
        animation: `pipelineStageIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${enterDelayMs}ms both`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      <Box
        onClick={onClick}
        sx={{
          cursor: onClick ? "pointer" : "default",
          display: "flex",
          alignItems: "center",
          gap: 0.85,
          px: 1.1,
          py: 1.15,
          width: "100%",
          minHeight: 68,
          boxSizing: "border-box",
          backgroundColor: colors.chipBg,
          borderRadius: "12px",
          border: `1.5px solid ${colors.accent}`,
          boxShadow: baseShadow,
          opacity: isDim ? 0.4 : 1,
          transformStyle: "preserve-3d",
          transform: isHighlight
            ? "translateY(-4px) rotateX(4deg) scale(1.03)"
            : "translateY(0) rotateX(0) scale(1)",
          transition:
            "box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease, border-color 0.25s ease",
          "&:hover": {
            borderColor: colors.accent,
            boxShadow: hoverShadow,
            opacity: 1,
            transform: onClick
              ? "translateY(-7px) rotateX(6deg) rotateY(-3deg) scale(1.035)"
              : isDim
                ? "none"
                : "translateY(-4px) rotateX(4deg) scale(1.02)",
            "& .pipeline-stage-icon": {
              transform: "translateZ(16px) scale(1.08)",
              boxShadow: `0 6px 12px ${colors.accent}40`,
            },
          },
          "&:active": onClick
            ? {
                transform: "translateY(-2px) rotateX(2deg) scale(0.99)",
                boxShadow: `0 4px 12px ${colors.accent}22, 0 2px 6px rgba(15,23,42,0.08)`,
              }
            : undefined,
          "@media (prefers-reduced-motion: reduce)": {
            transition: "box-shadow 0.2s ease, opacity 0.2s ease",
            "&:hover": {
              transform: "none",
              boxShadow: hoverShadow,
            },
            "&:active": {
              transform: "none",
            },
          },
        }}
      >
        <Box
          className="pipeline-stage-icon"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            backgroundColor: colors.iconBg,
            color: colors.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 3px 8px ${colors.accent}30`,
            transform: "translateZ(10px)",
            transition:
              "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease",
          }}
        >
          {icon}
        </Box>

        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: "'Poppins', sans-serif",
            fontSize: { xs: "12px", md: "11px" },
            fontWeight: 600,
            color: "#1f2937",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            transform: "translateZ(8px)",
          }}
        >
          {stage.label}
        </Typography>

        <ProgressRing
          value={stage.value}
          maxValue={maxValue}
          color={colors.accent}
          track={colors.track}
          animationDelayMs={enterDelayMs + 80}
        />
      </Box>
    </Box>
  );
};
